import i18n from '@/i18n';
import { MINUTES_IN_HOUR } from '@/lib/date-time/constants';
import { PlainDate } from '@/lib/date-time/PlainDate';
import { dateInPeriod, isSameDate } from '@/lib/filters/dateFilters';
import { belongsToEmployee } from '@/lib/filters/employeeFilters';
import { AbsenceStatusEnum } from '@/lib/reports/time-off/absence/absenceReportFilters';
import { isWorkingDay } from '@/lib/workPatternFunctions';
import store from '@/store';
import {
  earliestDate,
  isFuture,
  isPast,
  latestDate,
  startOfDay,
  startOfNextDay,
} from '@/util/dateArithmetic';
import { endDateToDateTime, startDateToDateTime } from '@/util/dateFunctions';
import { roundToStep } from '@/util/mathFunctions';
import { keyOfFirstTruthyValue } from '@/util/objectFunctions';
import spacetime from 'spacetime';
import {
  Absence,
  Employee,
  Shift,
  WorkPattern,
  WorkPatternOverride,
} from '../../../api/v1';

export const checkAbsence = (shift: Shift, absences: Absence[]): boolean => {
  const { timezone } = store.getters;
  const absenceFound = absences
    .filter((absence) => absence.employeeId === shift.employeeId)
    .filter((absence) => {
      const shiftStart = spacetime(shift.startsAt, timezone);
      const shiftEnd = spacetime(shift.endsAt, timezone);
      let absenceStart = spacetime(absence.startedOn.toString(), timezone);
      let absenceEnd = absence.endedOn
        ? spacetime(absence.endedOn.toString(), timezone)
        : null;

      if (absenceEnd && !absence.endedMidday) {
        absenceEnd = absenceEnd.add(1, 'day');
      }

      if (absence.startedMidday) {
        absenceStart = absenceStart.time('12:00:00');
      }

      if (absenceEnd && absence.endedMidday) {
        absenceEnd = absenceEnd.time('12:00:00');
      }

      if (!absenceEnd) {
        return (
          shiftStart.isAfter(absenceStart) && shiftEnd.isAfter(absenceStart)
        );
      }

      return shiftStart.isBefore(absenceEnd) && shiftEnd.isAfter(absenceStart);
    });

  return !!absenceFound.length;
};

export const absenceStartDate = (
  { startedMidday, startedOn }: Absence,
  timezone: string,
): Date => {
  return startDateToDateTime(startedOn, startedMidday, timezone);
};

// Returns an exclusive end date given the absence endedOn and endsMidday values
export const absenceEndDate = (
  { endedMidday, endedOn }: Absence,
  timezone: string,
): Date | null => {
  if (!endedOn) {
    return null;
  }
  return endDateToDateTime(endedOn, endedMidday, timezone);
};

export const totalDaysLost = (absences: Absence[]) =>
  absences.reduce((total, { lost }) => total + lost, 0);

export const getAbsenceStatus = (
  absence: Absence,
  timezone: string,
): string => {
  const startDate = absenceStartDate(absence, timezone);
  const endDate = absenceEndDate(absence, timezone);
  return keyOfFirstTruthyValue({
    [AbsenceStatusEnum.Upcoming]: isFuture(startDate),
    [AbsenceStatusEnum.Ongoing]:
      !isFuture(startDate) && (!endDate || isFuture(endDate)),
    [AbsenceStatusEnum.Closed]: endDate && isPast(endDate),
  }) as string;
};

export const getAbsenceStatusLabel = (
  absence: Absence,
  timezone: string,
): string =>
  ({
    [AbsenceStatusEnum.Upcoming]: i18n.tc('label.upcoming'),
    [AbsenceStatusEnum.Ongoing]: i18n.tc('label.ongoing'),
    [AbsenceStatusEnum.Closed]: i18n.tc('label.closed'),
  }[getAbsenceStatus(absence, timezone)]);

/**
 * This function will return the total days covered by an absence
 * */
export const getAbsenceTotalDaysCovered = (
  absence: Absence,
  timezone: string,
): number => {
  const absenceStart = absenceStartDate(absence, timezone);
  const absenceEnd = absenceEndDate(absence, timezone);

  if (isFuture(absenceStart) && !absenceEnd) {
    return 0;
  }

  return roundToStep(
    spacetime(absenceStart, timezone).diff(
      spacetime(absenceEnd, timezone),
      'hour',
    ) / 24,
    0.5,
  );
};

/**
 * This function will return the paid hours for a specified date within an absence
 */
export const getAbsencePaidHoursForDate = (
  absence: Absence,
  date: Date,
  employee: Employee,
  workPattern: WorkPattern,
  workPatternOverrides: WorkPatternOverride[],
  timezone: string,
): number => {
  const absenceStart = absenceStartDate(absence, timezone);
  const absenceEnd = absenceEndDate(absence, timezone);
  if (
    !absence.paid ||
    !dateInPeriod(
      date,
      startOfDay(absenceStart, timezone),
      absenceEnd ?? startOfNextDay(new Date(), timezone),
    )
  ) {
    return 0;
  }
  const employeeWorksDay = isWorkingDay(
    workPattern,
    workPatternOverrides,
    date,
    timezone,
  );

  const isHalfDay =
    (absence.startedMidday &&
      spacetime(absenceStart, timezone).isSame(
        spacetime(date, timezone),
        'day',
      )) ||
    (absence.endedMidday &&
      absence.endedOn &&
      spacetime(absenceEnd, timezone).isSame(spacetime(date, timezone), 'day'));

  return employeeWorksDay
    ? employee.leaveHoursUsedPerDay / (isHalfDay ? 2 : 1)
    : 0;
};

/**
 * This function will return the paid minutes for a specified date within an absence
 * NOTE: This is mainly use for timesheet functions
 */
export const getAbsencePaidMinutesForDate = (
  absence: Absence,
  date: Date,
  employee: Employee,
  workPattern: WorkPattern,
  workPatternOverrides: WorkPatternOverride[],
  timezone: string,
): number =>
  getAbsencePaidHoursForDate(
    absence,
    date,
    employee,
    workPattern,
    workPatternOverrides,
    timezone,
  ) * MINUTES_IN_HOUR;

export const getAbsenceRangePaidHours = (
  absence: Absence,
  start: PlainDate,
  end: PlainDate,
  employee: Employee,
  workPattern: WorkPattern,
  workPatternOverrides: WorkPatternOverride[],
  timezone: string,
): number => {
  if (!absence.paid) {
    return 0;
  }

  const minDate = latestDate([
    startOfDay(absenceStartDate(absence, timezone), timezone),
    start.toDate(timezone),
  ]);
  const maxDate = earliestDate([
    startOfNextDay(absenceEndDate(absence, timezone), timezone),
    end.toDate(timezone),
  ]);

  return (
    spacetime(minDate, timezone)
      .every('day', spacetime(maxDate, timezone), 1)
      .reduce((acc, date) => {
        const paidMinutesForDate = getAbsencePaidMinutesForDate(
          absence,
          date.toNativeDate(),
          employee,
          workPattern,
          workPatternOverrides,
          timezone,
        );
        return acc + paidMinutesForDate;
      }, 0) / 60
  );
};

export type AbsenceChunk = {
  id: number;
  startsAt: Date;
  endsAt: Date;
  absence: Absence;
};

export const getEmployeeAbsenceChunks = (
  employeeId: number,
  absences: Absence[],
  timezone: string,
  visibleStart: Date,
  visibleEnd: Date,
): AbsenceChunk[] => {
  const employeeAbsences = absences.filter(belongsToEmployee(employeeId));
  return employeeAbsences.reduce((arr, absence) => {
    const datesCoveredByAbsence = spacetime(
      latestDate([
        visibleStart,
        startOfDay(absence.startedOn.toDate(timezone), timezone),
      ]),
      timezone,
    ).every(
      'day',
      spacetime(
        earliestDate([
          visibleEnd,
          startOfNextDay(
            absence.endedOn?.toDate(timezone) || visibleEnd,
            timezone,
          ),
        ]),
      ),
      1,
    );

    const chunkedAbsences = datesCoveredByAbsence.map((spaceDate) => ({
      id: absence.id,
      startsAt: isSameDate(
        spaceDate.toNativeDate(),
        timezone,
        'startsAt',
      )({ startsAt: absenceStartDate(absence, timezone) })
        ? absenceStartDate(absence, timezone)
        : startOfDay(spaceDate.toNativeDate(), timezone),
      endsAt:
        absence.endedOn &&
        isSameDate(
          spaceDate.toNativeDate(),
          timezone,
          'endsAt',
        )({ endsAt: absenceEndDate(absence, timezone) })
          ? absenceEndDate(absence, timezone)
          : startOfNextDay(spaceDate.toNativeDate(), timezone),
      absence,
    }));

    return [...arr, ...chunkedAbsences];
  }, []);
};
